import React from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';

const BankWalaTrackHome = () => {
  const tabItems = [
    { title: 'Accountability', icon: 'fa-solid fa-check-circle' },
    { title: 'Transparency', icon: 'fa-solid fa-eye' },
    { title: 'Maturity', icon: 'fa-solid fa-chart-line' },
    { title: 'Audits', icon: 'fa-solid fa-clipboard-check' },
    { title: 'AI Insights', icon: 'fa-solid fa-robot' },
    { title: 'Ranking', icon: 'fa-solid fa-star' },
    { title: 'Goals', icon: 'fa-solid fa-bullseye' },
    { title: 'Risk', icon: 'fa-solid fa-triangle-exclamation' },
    { title: 'People', icon: 'fa-solid fa-users' },
    { title: 'Finance', icon: 'fa-solid fa-indian-rupee-sign' },
    { title: 'Customer', icon: 'fa-solid fa-users' },
    { title: 'Compliance', icon: 'fa-solid fa-scale-balanced' },
    { title: 'Regulatory', icon: 'fa-solid fa-file-contract' },
    { title: 'NPA Management', icon: 'fa-solid fa-chart-line-down' },
    { title: 'Fraud Detection', icon: 'fa-solid fa-shield-halved' },
  ];

  const kpiCards = [
    { label: 'Trust Rank (TR)', value: '3.8 / 5', trend: '+0.4 this quarter' },
    { label: 'Loan Approval Rate', value: '92%', trend: '+5% vs last month' },
    { label: 'Goal Completion', value: '72%', trend: '+12% vs last cycle' },
    { label: 'Audit Readiness', value: 'Level 3 of 5', trend: 'Next: External L4' },
    { label: 'NPA Ratio', value: '2.1%', trend: '-0.3% vs last quarter' },
    { label: 'Customer Satisfaction', value: '4.2 / 5', trend: '+0.2 this month' },
    { label: 'Deposit Growth', value: '12%', trend: '+2% YoY' },
    { label: 'CASA Ratio', value: '45%', trend: '+1.5% QoQ' },
    { label: 'Net Interest Margin (NIM)', value: '3.2%', trend: '+0.1% vs last quarter' },
    { label: 'Fraud Incidents', value: '15', trend: '-20% vs last month' },
  ];

  // Bar chart: process scores
  const processBarData = {
    labels: ['Operations', 'Finance', 'People', 'Compliance', 'Customer', 'Risk', 'Deposits'],
    datasets: [
      {
        label: 'Current Score',
        data: [72, 58, 64, 81, 78, 75, 82],
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // Tailwind-like blue-500
      },
    ],
  };

  const processBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#9CA3AF',
        },
      },
      x: {
        ticks: {
          color: '#9CA3AF',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#E5E7EB',
        },
      },
    },
  };

  // Doughnut chart: goal status
  const goalDoughnutData = {
    labels: ['On Track', 'Delayed', 'At Risk'],
    datasets: [
      {
        data: [55, 25, 20],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)', // green
          'rgba(59, 130, 246, 0.8)', // blue
          'rgba(239, 68, 68, 0.8)', // red
        ],
      },
    ],
  };

  const goalDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#E5E7EB',
        },
      },
    },
  };

  // Line chart: Deposit Growth trend over months
  const depositGrowthLineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Deposit Growth (%)',
        data: [8.5, 9.2, 9.8, 10.5, 11.2, 11.8, 12.0],
        borderColor: 'rgba(16, 185, 129, 1)', // green for growth
        backgroundColor: 'rgba(16, 185, 129, 0.3)',
        tension: 0.35,
        fill: true,
      },
    ],
  };

  const depositGrowthLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#9CA3AF',
        },
      },
      x: {
        ticks: {
          color: '#9CA3AF',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#E5E7EB',
        },
      },
    },
  };

  // Line chart: Credit Growth trend over months
  const creditGrowthLineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Credit Growth (%)',
        data: [10.2, 10.8, 11.5, 12.1, 12.7, 13.2, 13.5],
        borderColor: 'rgba(251, 191, 36, 1)', // amber for credit
        backgroundColor: 'rgba(251, 191, 36, 0.3)',
        tension: 0.35,
        fill: true,
      },
    ],
  };

  const creditGrowthLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#9CA3AF',
        },
      },
      x: {
        ticks: {
          color: '#9CA3AF',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#E5E7EB',
        },
      },
    },
  };

  // Dummy table data for recent audits / events
  const recentEvents = [
    { date: '2025-10-03', type: 'KYC Audit', area: 'Operations', status: 'Completed' },
    { date: '2025-10-10', type: 'Goal Review', area: 'People', status: 'In Progress' },
    { date: '2025-10-18', type: 'RBI Compliance Audit', area: 'Compliance', status: 'Scheduled' },
    { date: '2025-10-22', type: 'AI Risk Insight', area: 'Finance', status: 'Action Needed' },
    { date: '2025-11-05', type: 'NPA Review', area: 'Risk', status: 'In Progress' },
    { date: '2025-11-15', type: 'Fraud Alert Scan', area: 'Fraud Detection', status: 'Completed' },
  ];

  return (
    <div className="min-h-screen bg-black text-white px-3 py-4 lg:px-6">
      {/* Header */}
      <header className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-yellow-200 text-left">BankWala TRACK</h1>
          <p className="text-xs lg:text-sm text-white mt-1">
            Transparency, Responsibility & Accountability Check Kit
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs lg:text-sm text-gray-300">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-900/40 px-3 py-1 border border-blue-500/40">
            <i className="fa-solid fa-shield-halved" />
            TR:{' '}
            <span className="font-semibold text-blue-300">3.8 · Gold</span>
          </span>
          <span className="hidden lg:inline-flex items-center gap-1 rounded-full bg-gray-900 px-3 py-1 border border-gray-700">
            <i className="fa-regular fa-circle-dot" />
            Live Demo Mode
          </span>
        </div>
      </header>

      {/* Top-level tabs grid: 3 per row on mobile, 6 on larger screens */}
      <section className="mb-2">
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-1 place-items-center">
          {tabItems.map((tab, index) => (
            <button
              key={tab.title + index}
              className="flex flex-col items-center justify-center rounded-md bg-gray-900/80 border border-gray-700/70 px-2 py-3 hover:border-blue-500 hover:bg-gray-900 transition text-center"
              type="button"
            >
              <i className={`${tab.icon} text-base lg:text-lg text-blue-300 mb-1`} />
              <span className="text-[0.67rem] lg:text-xs text-gray-200 leading-tight">
                {tab.title}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* KPI cards - Adjusted grid for more cards */}
      <section className="mb-5 grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpiCards.map((card) => (
          <div
            key={card.label}
            className="rounded-md bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/40 border border-gray-800 px-3 py-3 flex flex-col justify-between"
          >
            <p className="text-[0.7rem] lg:text-xs text-gray-400">{card.label}</p>
            <p className="text-lg lg:text-xl font-semibold text-blue-300 mt-1">{card.value}</p>
            <p className="text-[0.68rem] lg:text-xs text-emerald-400 mt-1">{card.trend}</p>
          </div>
        ))}
      </section>

      {/* Charts layout: stacked on mobile, side-by-side on large screens */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-2 place-items-center">
        {/* Process Scores Bar Chart */}
        <div className="rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col min-h-[260px]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm lg:text-base font-medium text-blue-300">Process Scores</h2>
            <span className="text-[0.65rem] text-gray-400">Last self-audit</span>
          </div>
          <div className="flex-1 min-h-[200px]">
            <Bar data={processBarData} options={processBarOptions} />
          </div>
        </div>

        {/* Deposit Growth Trend Line Chart */}
        <div className="rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col min-h-[260px]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm lg:text-base font-medium text-blue-300">Deposit Growth Trend</h2>
            <span className="text-[0.65rem] text-gray-400">Monthly YoY</span>
          </div>
          <div className="flex-1 min-h-[200px]">
            <Line data={depositGrowthLineData} options={depositGrowthLineOptions} />
          </div>
        </div>
      </section>

      {/* Credit Growth Chart Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-2 place-items-center">
        {/* Credit Growth Trend Line Chart */}
        <div className="rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col min-h-[260px]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm lg:text-base font-medium text-blue-300">Credit Growth Trend</h2>
            <span className="text-[0.65rem] text-gray-400">Monthly YoY</span>
          </div>
          <div className="flex-1 min-h-[200px]">
            <Line data={creditGrowthLineData} options={creditGrowthLineOptions} />
          </div>
        </div>

        {/* Placeholder for potential second chart or empty */}
        <div className="hidden lg:block rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col min-h-[260px]">
          {/* Can add another chart here in future */}
        </div>
      </section>

      {/* Goal status + recent events */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-2 place-items-center">
        {/* Goal Status Doughnut */}
        <div className="rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col min-h-[260px]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm lg:text-base font-medium text-blue-300">Goal Status</h2>
            <span className="text-[0.65rem] text-gray-400">Current cycle</span>
          </div>
          <div className="flex-1 flex items-center justify-center min-h-[200px]">
            <div className="w-40 h-40 lg:w-52 lg:h-52">
              <Doughnut data={goalDoughnutData} options={goalDoughnutOptions} />
            </div>
          </div>
        </div>

        {/* Recent Events List */}
        <div className="rounded-md bg-gray-900 border border-gray-800 p-3 lg:p-4 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm lg:text-base font-medium text-blue-300">Recent Accountability Events</h2>
            <span className="text-[0.65rem] text-gray-400">Timeline</span>
          </div>
          <div className="flex-1 overflow-y-auto max-h-64 mt-1">
            <ul className="divide-y divide-gray-800 text-xs lg:text-sm">
              {recentEvents.map((event, idx) => (
                <li key={idx} className="py-2 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-gray-200 font-medium">{event.type}</p>
                    <p className="text-gray-400 text-[0.7rem] lg:text-xs">Area: {event.area}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[0.7rem] lg:text-xs text-gray-400">{event.date}</p>
                    <p
                      className={`text-[0.7rem] lg:text-xs mt-1 ${
                        event.status === 'Completed'
                          ? 'text-emerald-400'
                          : event.status === 'Scheduled'
                          ? 'text-blue-400'
                          : event.status === 'In Progress'
                          ? 'text-amber-300'
                          : 'text-red-400'
                      }`}
                    >
                      {event.status}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BankWalaTrackHome;